/**
 * Goal Selection Wizard
 * Modal for selecting a goal-driven journey path
 */

import { useState } from 'react'
import { X, Check, Rocket, Search, Target, Map as MapIcon, Sprout, ChevronRight } from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import type { JourneyGoal, PhaseMode } from '@ispot/shared'

interface GoalSelectionWizardProps {
  isOpen: boolean
  onClose: () => void
  onGoalSelected?: (goalId: string) => void
}

// Goal ID to icon mapping
const goalIcons: Record<string, React.ReactNode> = {
  'validate-new-idea': <Sprout className="w-6 h-6" />,
  'diagnose-stalled-growth': <Search className="w-6 h-6" />,
  'sharpen-positioning': <Target className="w-6 h-6" />,
  'prioritize-roadmap': <MapIcon className="w-6 h-6" />,
  'expand-new-market': <Rocket className="w-6 h-6" />,
}

// Phase mode colors
const modeColors: Record<PhaseMode, { bg: string; text: string; label: string }> = {
  full: { bg: 'bg-market/20', text: 'text-market', label: 'Full' },
  check: { bg: 'bg-business/20', text: 'text-business', label: 'Check' },
  review: { bg: 'bg-solution/20', text: 'text-solution', label: 'Review' },
  skip: { bg: 'bg-warm-200', text: 'text-text-muted', label: 'Skip' },
}

export function GoalSelectionWizard({ isOpen, onClose, onGoalSelected }: GoalSelectionWizardProps) {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { framework, selectGoal, isUpdating } = useJourneyStore()
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null)
  const [showPhasePreview, setShowPhasePreview] = useState(false)

  if (!isOpen || !framework) return null

  const goals = framework.goals || []
  const selectedGoal = goals.find(g => g.id === selectedGoalId)

  const handleConfirm = async () => {
    if (!selectedGoalId || !currentAccount?.id || !currentProject?.id) return

    await selectGoal(currentAccount.id, currentProject.id, selectedGoalId)
    onGoalSelected?.(selectedGoalId)
    onClose()
  }

  const getPhaseCount = (goal: JourneyGoal): number => {
    return Object.values(goal.phaseConfig).filter(mode => mode !== 'skip').length
  }

  const getIncludedPhases = (goal: JourneyGoal) => {
    if (!framework) return []
    return framework.phases
      .filter(p => goal.phaseConfig[p.id] !== 'skip')
      .sort((a, b) => a.order - b.order)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-warm-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div>
            <h2 className="text-xl font-display font-semibold text-text">Choose Your Journey Goal</h2>
            <p className="text-sm text-text-muted mt-1">
              Select the goal that best matches your current situation
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-warm-100 text-text-muted hover:text-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Goal Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.map((goal) => {
              const isSelected = selectedGoalId === goal.id
              const phaseCount = getPhaseCount(goal)

              return (
                <button
                  key={goal.id}
                  onClick={() => setSelectedGoalId(goal.id)}
                  className={`
                    relative p-4 rounded-xl border-2 text-left transition-all duration-200
                    ${isSelected
                      ? 'border-market bg-market/5 shadow-md'
                      : 'border-border-subtle bg-warm-50 hover:border-border hover:bg-warm-100'
                    }
                  `}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-market flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Goal Icon */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`
                      w-10 h-10 rounded-lg flex items-center justify-center
                      ${isSelected ? 'bg-market/20 text-market' : 'bg-warm-100 text-text-secondary'}
                    `}>
                      {goalIcons[goal.id] || <Target className="w-5 h-5" />}
                    </div>
                  </div>

                  {/* Goal Name */}
                  <h3 className="font-semibold text-text mb-1">{goal.name}</h3>

                  {/* Short Description */}
                  <p className="text-sm text-text-muted mb-3">
                    {goal.shortDescription || goal.description}
                  </p>

                  {/* Goal Type Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`
                      text-xs px-2 py-0.5 rounded-full
                      ${goal.type === 'greenfield'
                        ? 'bg-market/10 text-market'
                        : 'bg-business/10 text-business'
                      }
                    `}>
                      {goal.type === 'greenfield' ? 'New Product' : 'Existing Product'}
                    </span>
                    <span className="text-xs text-text-muted">
                      {phaseCount} phases
                    </span>
                  </div>

                  {/* Recommended For */}
                  {goal.recommendedFor && goal.recommendedFor.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {goal.recommendedFor.slice(0, 3).map((useCase, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-0.5 rounded bg-warm-100 text-text-muted"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Phase Preview (when goal selected) */}
          {selectedGoal && (
            <div className="mt-6 p-4 rounded-xl bg-warm-100 border border-border-subtle">
              <button
                onClick={() => setShowPhasePreview(!showPhasePreview)}
                className="flex items-center justify-between w-full text-left"
              >
                <h4 className="font-medium text-text">
                  Journey Phases for "{selectedGoal.name}"
                </h4>
                <ChevronRight className={`w-5 h-5 text-text-muted transition-transform ${showPhasePreview ? 'rotate-90' : ''}`} />
              </button>

              {showPhasePreview && (
                <div className="mt-4 space-y-2">
                  {getIncludedPhases(selectedGoal).map((phase, index) => {
                    const mode = selectedGoal.phaseConfig[phase.id] as PhaseMode
                    const modeStyle = modeColors[mode]

                    return (
                      <div
                        key={phase.id}
                        className="flex items-center gap-3 p-3 rounded-lg bg-warm-50"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white"
                          style={{ backgroundColor: phase.color }}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-text truncate">{phase.name}</p>
                          <p className="text-xs text-text-muted truncate">{phase.objective}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${modeStyle.bg} ${modeStyle.text}`}>
                          {modeStyle.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle bg-warm-100">
          <p className="text-sm text-text-muted">
            {selectedGoalId
              ? `Selected: ${selectedGoal?.name}`
              : 'Select a goal to continue'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedGoalId || isUpdating}
              className={`
                px-6 py-2 rounded-lg text-sm font-medium transition-all
                ${selectedGoalId && !isUpdating
                  ? 'bg-market text-white hover:bg-market/90'
                  : 'bg-warm-200 text-text-muted cursor-not-allowed'
                }
              `}
            >
              {isUpdating ? 'Saving...' : 'Start Journey'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GoalSelectionWizard
